<template>
  <div class="flex h-screen bg-gray-100 text-gray-900">
    <!-- Sidebar -->
    <aside class="w-64 bg-white border-r flex flex-col">
      <div class="p-4 border-b font-semibold text-lg">ChatGPT</div>
      <div class="flex-1 overflow-y-auto">
        <ul>
          <li
              v-for="(conv, index) in conversations"
              :key="index"
              @click="selectConversation(index)"
              :class="[
              'p-4 cursor-pointer hover:bg-gray-100',
              selectedConvIndex === index ? 'bg-gray-200 font-semibold' : ''
            ]"
          >
            {{ conv.title }}
          </li>
        </ul>
      </div>
      <button class="p-4 border-t hover:bg-gray-100 text-left" @click="newConversation">
        + New Chat
      </button>
    </aside>

    <!-- Chat area -->
    <main class="flex-1 flex flex-col">
      <header class="p-4 border-b font-semibold bg-white">
        {{ currentConversation?.title || 'New Chat' }}
      </header>

      <!-- Chat Messages -->
      <div
          ref="chatContainer"
          class="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50 flex flex-col"
      >
        <div
            v-for="(msg, index) in currentConversation?.messages || []"
            :key="index"
            :class="[
            'p-4 rounded-lg max-w-3xl whitespace-pre-wrap',
            msg.role === 'user' ? 'bg-blue-100 self-end' : 'bg-white border self-start'
          ]"
        >
          <div v-html="renderMarkdown(msg.content)" class="prose max-w-full" />
        </div>

        <!-- Streaming response -->
        <div v-if="streamingResponse" class="bg-white border p-4 rounded-lg max-w-3xl self-start">
          <div v-html="renderMarkdown(streamingResponse)" class="prose max-w-full" />
          <div class="text-gray-400 text-xs italic">Assistant is typing...</div>
        </div>
      </div>

      <!-- Input -->
      <footer class="p-4 border-t bg-white">
        <form @submit.prevent="sendMessage" class="flex items-center gap-2">
          <textarea
              v-model="message"
              placeholder="Send a message..."
              rows="1"
              class="flex-1 resize-none rounded-lg border p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
              type="submit"
              class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Send
          </button>
        </form>
      </footer>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import { marked } from 'marked'

const conversations = ref([
  { title: 'Welcome Chat', messages: [{ role: 'user', content: 'Hello!' }, { role: 'assistant', content: 'Hi! How can I help you today?' }] },
])

const selectedConvIndex = ref(0)
const message = ref('')
const streamingResponse = ref('')
const chatContainer = ref(null)

const currentConversation = computed(() => conversations.value[selectedConvIndex.value])

function renderMarkdown(content) {
  return marked.parse(content || '')
}

function scrollToBottom() {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    }
  })
}

function sendMessage() {
  if (!message.value.trim()) return

  const userMsg = { role: 'user', content: message.value }
  currentConversation.value.messages.push(userMsg)
  const assistantMsg = { role: 'assistant', content: '' }

  // Simulate streaming
  const fullResponse = simulateResponse(message.value)
  streamingResponse.value = ''
  let i = 0

  const interval = setInterval(() => {
    if (i < fullResponse.length) {
      streamingResponse.value += fullResponse[i]
      i++
      scrollToBottom()
    } else {
      clearInterval(interval)
      assistantMsg.content = streamingResponse.value
      currentConversation.value.messages.push(assistantMsg)
      streamingResponse.value = ''
      scrollToBottom()
    }
  }, 20)

  message.value = ''
  scrollToBottom()
}

function simulateResponse(input) {
  // Replace this with a real streaming API call if desired
  return `Sure! Here's a **markdown** response with _formatting_ and a list:\n\n- Point one\n- Point two\n\n> You asked: "${input}"`
}

function newConversation() {
  conversations.value.unshift({
    title: `Chat ${conversations.value.length + 1}`,
    messages: [],
  })
  selectedConvIndex.value = 0
}

function selectConversation(index) {
  selectedConvIndex.value = index
}
</script>

<style scoped>
/* Optional: Tailwind Typography plugin styles for prose */
@import 'https://cdn.jsdelivr.net/npm/@tailwindcss/typography@0.5.9/dist/typography.min.css';
</style>
